import { useEffect, useMemo, useState } from 'react';
import { seriesAPI, divisionsAPI, teamsAPI, groundsAPI, fixturesAPI, conditionsAPI } from '../../services/api';
import { PageHeader, SectionTitle, Spinner, EmptyState } from '../../components/ui';
import DateRangePicker from '../../components/DateRangePicker';
import toast from 'react-hot-toast';

// Ground balance report: teams × grounds count matrix.
// Filters: series, division, teams (multi), months (multi), date range.
// All aggregation is client-side from /api/fixtures — no new backend endpoint.

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function GroundBalancePage() {
  const [seriesList, setSeriesList] = useState([]);
  const [seriesId, setSeriesId] = useState('');
  const [divisions, setDivisions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [grounds, setGrounds] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [groundRules, setGroundRules] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [divisionId, setDivisionId] = useState('');
  const [teamIds, setTeamIds] = useState([]);       // empty = all
  const [months, setMonths] = useState([]);         // empty = all; ints 0-11
  const [dateRange, setDateRange] = useState({ start: null, end: null });

  // Initial: series list
  useEffect(() => {
    (async () => {
      try {
        const { data } = await seriesAPI.list();
        setSeriesList(data);
        if (data.length && !seriesId) {
          const active = data.find(s => s.status === 'active') || data[0];
          setSeriesId(active.id);
        }
      } catch (e) { toast.error('Failed to load series'); }
    })();
  }, []); // eslint-disable-line

  // When series changes, load divisions/teams/grounds/fixtures
  useEffect(() => {
    if (!seriesId) return;
    (async () => {
      setLoading(true);
      try {
        const [{ data: divs }, { data: tms }, { data: gs }, { data: fs }, { data: conds }] = await Promise.all([
          divisionsAPI.list(seriesId),
          teamsAPI.list({ series_id: seriesId }),
          groundsAPI.list(),
          fixturesAPI.list({ series_id: seriesId }),
          conditionsAPI.list({ series_id: seriesId }),
        ]);
        setDivisions(divs);
        setTeams(tms);
        setGrounds(gs);
        setFixtures(fs);
        const gr = (conds || []).filter(c => c.condition_type === 'ground_rule').map(c => ({
          ground_id: c.value?.ground_id,
          premier_priority: !!c.value?.premier_priority,
          allowed_division_ids: Array.isArray(c.value?.allowed_division_ids) ? c.value.allowed_division_ids : [],
          division_restriction: c.value?.division_restriction || 'none',
        })).filter(r => r.ground_id);
        setGroundRules(gr);
        // Reset filters on series change
        setDivisionId('');
        setTeamIds([]);
        setMonths([]);
        setDateRange({ start: null, end: null });
      } catch (e) { toast.error('Load failed'); }
      finally { setLoading(false); }
    })();
  }, [seriesId]);

  // Teams filtered by division (for the teams multi-select + report rows)
  const teamsInDiv = useMemo(
    () => divisionId ? teams.filter(t => t.division_id === divisionId) : teams,
    [teams, divisionId]
  );

  // Fixtures filtered by all active filters
  const filteredFixtures = useMemo(() => {
    const teamSet = teamIds.length ? new Set(teamIds) : null;
    const monthSet = months.length ? new Set(months) : null;
    const startStr = dateRange.start ? toYMD(dateRange.start) : null;
    const endStr   = dateRange.end   ? toYMD(dateRange.end)   : null;
    return fixtures.filter(f => {
      if (f.status !== 'confirmed') return false;
      if (!f.match_date || !f.ground_id) return false;
      if (divisionId && f.division_id !== divisionId) return false;
      const d = String(f.match_date).split('T')[0];
      if (startStr && d < startStr) return false;
      if (endStr && d > endStr) return false;
      if (monthSet) {
        const m = new Date(d + 'T12:00:00').getMonth();
        if (!monthSet.has(m)) return false;
      }
      if (teamSet && !teamSet.has(f.home_team_id) && !teamSet.has(f.away_team_id)) return false;
      return true;
    });
  }, [fixtures, divisionId, teamIds, months, dateRange]);

  // Count matrix: teamId → { groundId → count, total }
  const counts = useMemo(() => {
    const m = {};
    filteredFixtures.forEach(f => {
      [f.home_team_id, f.away_team_id].forEach(tid => {
        if (!tid) return;
        if (!m[tid]) m[tid] = { _total: 0 };
        m[tid][f.ground_id] = (m[tid][f.ground_id] || 0) + 1;
        m[tid]._total += 1;
      });
    });
    return m;
  }, [filteredFixtures]);

  // Rows: teams in (filtered division, filtered team selection), sorted alpha
  const rowTeams = useMemo(() => {
    const pool = teamsInDiv;
    const filtered = teamIds.length ? pool.filter(t => teamIds.includes(t.id)) : pool;
    return [...filtered].sort((a, b) => (a.short_name || a.name).localeCompare(b.short_name || b.name));
  }, [teamsInDiv, teamIds]);

  // Grounds eligible for the selected division, mirroring scheduler's canUseGround.
  // When a division is selected we show *all* grounds the division CAN use —
  // not just the ones with fixtures — so Premier shows Troy even if filters
  // leave zero games there, and hides Northville which the division can't use.
  const groundRuleMap = useMemo(() => {
    const m = {}; groundRules.forEach(r => { m[r.ground_id] = r; }); return m;
  }, [groundRules]);

  const canDivisionUseGround = (divId, ground) => {
    if (!divId) return true;
    const div = divisions.find(d => d.id === divId);
    const isPremier = (div?.name || '').toLowerCase() === 'premier';
    const isPremierGround = ground.ground_type === 'premier';
    const rule = groundRuleMap[ground.id];
    if (isPremier) return isPremierGround;
    if (!isPremierGround) return true;
    if (rule?.division_restriction === 'premier_only') return false;
    if (rule?.premier_priority && rule.allowed_division_ids?.length > 0) {
      return rule.allowed_division_ids.includes(divId);
    }
    return true;
  };

  // Columns: grounds eligible for the selected division (or all grounds
  // present in fixtures if no division is selected).
  const displayGrounds = useMemo(() => {
    if (divisionId) {
      return [...grounds]
        .filter(g => canDivisionUseGround(divisionId, g))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    const usedGIds = new Set(filteredFixtures.map(f => f.ground_id).filter(Boolean));
    return [...grounds]
      .filter(g => usedGIds.has(g.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [grounds, filteredFixtures, divisionId, divisions, groundRuleMap]); // eslint-disable-line

  const toggleInArray = (arr, v) => arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v];

  return (
    <div>
      <PageHeader
        icon="🎯"
        title="Ground Balance"
        subtitle="How many games each team plays at each ground"
      />

      {/* Filter bar */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end',
        padding: '12px 14px', background: 'var(--card)', borderRadius: 8,
        border: '1px solid var(--border)', marginBottom: 14,
      }}>
        <FilterField label="Series">
          <select className="input" value={seriesId} onChange={e => setSeriesId(e.target.value)} style={{ fontSize: 12 }}>
            {seriesList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </FilterField>

        <FilterField label="Division">
          <select className="input" value={divisionId} onChange={e => { setDivisionId(e.target.value); setTeamIds([]); }} style={{ fontSize: 12 }}>
            <option value="">All divisions</option>
            {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </FilterField>

        <FilterField label="Date range">
          <DateRangePicker
            start={dateRange.start}
            end={dateRange.end}
            onChange={setDateRange}
            size="sm"
          />
        </FilterField>

        <FilterField label="Months">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {MONTH_NAMES.map((nm, i) => {
              const on = months.includes(i);
              return (
                <button key={i} type="button"
                  onClick={() => setMonths(toggleInArray(months, i))}
                  style={{
                    fontSize: 10, padding: '3px 7px', borderRadius: 4,
                    background: on ? 'var(--accent)' : 'var(--surface)',
                    color: on ? 'white' : 'var(--mid)',
                    border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                    cursor: 'pointer',
                  }}>{nm}</button>
              );
            })}
          </div>
        </FilterField>

        <FilterField label={`Teams${teamIds.length ? ` (${teamIds.length})` : ''}`}>
          <TeamMultiSelect
            teams={teamsInDiv}
            selected={teamIds}
            onChange={setTeamIds}
          />
        </FilterField>

        {(divisionId || teamIds.length || months.length || dateRange.start || dateRange.end) && (
          <button
            type="button"
            onClick={() => { setDivisionId(''); setTeamIds([]); setMonths([]); setDateRange({ start: null, end: null }); }}
            style={{
              fontSize: 11, padding: '5px 10px', borderRadius: 5,
              background: 'var(--surface)', color: 'var(--red)', border: '1px solid var(--border)',
              cursor: 'pointer',
            }}
          >Clear filters</button>
        )}
      </div>

      {/* Summary */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
        <Summary label="Teams" value={rowTeams.length} />
        <Summary label="Grounds" value={displayGrounds.length} />
        <Summary label="Games" value={filteredFixtures.length} />
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>
      ) : !filteredFixtures.length ? (
        <EmptyState icon="🎯" title="No games match these filters"
          description="Try clearing filters or picking a different series" />
      ) : (
        <BalanceTable
          teams={rowTeams}
          grounds={displayGrounds}
          counts={counts}
        />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────────

function BalanceTable({ teams, grounds, counts }) {
  // Color scale: 0 = dim, ramps to accent. Max count across table for normalization.
  let max = 0;
  teams.forEach(t => { grounds.forEach(g => { max = Math.max(max, counts[t.id]?.[g.id] || 0); }); });
  const colorFor = (n) => {
    if (!n) return { bg: 'transparent', fg: 'var(--dim)' };
    const r = Math.min(1, n / Math.max(3, max));
    return {
      bg: `rgba(0, 200, 150, ${0.08 + r * 0.35})`,
      fg: r > 0.5 ? 'var(--accent)' : 'var(--text)',
    };
  };

  return (
    <div style={{
      overflow: 'auto', border: '1px solid var(--border)', borderRadius: 8,
      background: 'var(--card)', maxHeight: 'calc(100vh - 280px)',
    }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
        <thead>
          <tr style={{ background: 'var(--surface)' }}>
            <th style={{ ...headCell, position: 'sticky', left: 0, zIndex: 2, background: 'var(--surface)', minWidth: 150 }}>Team</th>
            {grounds.map(g => (
              <th key={g.id} style={{ ...headCell, minWidth: 90, textAlign: 'center' }}>
                {g.name}
              </th>
            ))}
            <th style={{ ...headCell, minWidth: 60, textAlign: 'center', background: 'var(--surface)' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {teams.map(t => {
            const row = counts[t.id] || {};
            return (
              <tr key={t.id}>
                <td style={{
                  ...bodyCell, position: 'sticky', left: 0, background: 'var(--card)',
                  fontWeight: 700, color: 'var(--text)',
                  fontFamily: "'Roboto Mono', monospace",
                }}>
                  {t.short_name || t.name}
                </td>
                {grounds.map(g => {
                  const n = row[g.id] || 0;
                  const c = colorFor(n);
                  return (
                    <td key={g.id} style={{
                      ...bodyCell, textAlign: 'center',
                      background: c.bg, color: c.fg,
                      fontWeight: n ? 600 : 400,
                      fontFamily: "'Roboto Mono', monospace",
                    }}>
                      {n || '—'}
                    </td>
                  );
                })}
                <td style={{
                  ...bodyCell, textAlign: 'center', fontWeight: 700,
                  color: 'var(--accent)', background: 'var(--surface)',
                  fontFamily: "'Roboto Mono', monospace",
                }}>
                  {row._total || 0}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TeamMultiSelect({ teams, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const label = !selected.length ? 'All teams'
    : selected.length === 1 ? teams.find(t => t.id === selected[0])?.short_name || '1 team'
    : `${selected.length} teams`;
  return (
    <div style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)} style={{
        fontSize: 12, padding: '6px 10px', borderRadius: 5,
        background: 'var(--surface)', color: 'var(--text)',
        border: '1px solid var(--border)', cursor: 'pointer', minWidth: 120,
        fontFamily: "'Roboto Mono', monospace", textAlign: 'left',
      }}>{label} ▾</button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 10 }} />
          <div style={{
            position: 'absolute', top: '100%', left: 0, zIndex: 11, marginTop: 4,
            background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 6,
            minWidth: 180, maxHeight: 280, overflow: 'auto',
            boxShadow: '0 8px 24px rgba(0,0,0,.3)', padding: 4,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px' }}>
              <button type="button" style={linkBtn} onClick={() => onChange(teams.map(t => t.id))}>Select all</button>
              <button type="button" style={linkBtn} onClick={() => onChange([])}>Clear</button>
            </div>
            {teams.sort((a, b) => (a.short_name || a.name).localeCompare(b.short_name || b.name)).map(t => {
              const on = selected.includes(t.id);
              return (
                <label key={t.id} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '4px 8px', borderRadius: 4, cursor: 'pointer',
                  background: on ? 'rgba(0,200,150,.08)' : 'transparent',
                }}>
                  <input type="checkbox" checked={on}
                    onChange={() => onChange(on ? selected.filter(x => x !== t.id) : [...selected, t.id])} />
                  <span style={{ fontSize: 11, fontFamily: "'Roboto Mono', monospace" }}>
                    {t.short_name || t.name}
                  </span>
                </label>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</span>
      {children}
    </div>
  );
}

function Summary({ label, value }) {
  return (
    <div style={{
      padding: '8px 14px', background: 'var(--card)', borderRadius: 6,
      border: '1px solid var(--border)', minWidth: 90,
    }}>
      <div style={{ fontSize: 10, color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--accent)', fontFamily: "'Roboto Mono', monospace" }}>{value}</div>
    </div>
  );
}

// ── utils ──
function toYMD(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const headCell = {
  padding: '8px 10px', fontWeight: 700, fontSize: 11,
  color: 'var(--mid)', textTransform: 'uppercase', letterSpacing: 0.4,
  borderBottom: '1px solid var(--border)', textAlign: 'left',
};
const bodyCell = { padding: '6px 10px', borderBottom: '1px solid var(--border)' };
const linkBtn = {
  fontSize: 10, background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--accent)', padding: 0,
};
